import { DependencyList, useCallback } from 'react'
import { buildSubmittedNotificationPackages, validateSaveDraft } from '../../../helpers'
import { ApiVersion } from '../../../../../enums/api-version.enum'
import { INotificationBuilderState } from '../../../interfaces/notification-builder-state.interface'
import { DomainDto } from '../../../../../dtos/domain'
import FlagList from '../../../../../structs/flags-list'
import { AppService, NotificationService } from '../../../../../services'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../../../stores/app'
import { AccountDto } from '../../../../../dtos/account.dto'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'
import { NotificationBuilderViewMode, NotificationBuilderViewType } from '../types'
import { NotificationTemplateService } from '../../../../../services/notification-template'
import { AccountNotificationService } from '../../../../../services/account-notification'
import { useNotificationBuilderViewBackToParentViewCallback } from './use-notification-builder-view-back-to-parent-view-callback'

type SubmitCallbackProps = {
    level: PlatformLevel
    formType: NotificationBuilderViewType
    formMode: NotificationBuilderViewMode
    domain: DomainDto
    account?: AccountDto
    builder: INotificationBuilderState
    requestedNotifId?: number
    requestedTemplateId?: number
    requestedTestId?: number
    validateSubmit(
        builder: INotificationBuilderState,
        domain: DomainDto,
        flags: FlagList,
        isPreview?: boolean,
        isDraft?: boolean,
        appService?: AppService,
    ): Promise<boolean>
    resolveParentRoute?(isDraft: boolean): string
    consumerOnSubmitCompletedHandler?(successful: boolean, isDraft: boolean): void | Promise<void>
    consumerSubmitHandler?(isDraft: boolean, builderState: INotificationBuilderState): void | Promise<void>
}

export const useNotificationBuilderViewSubmitCallback = (props: SubmitCallbackProps, deps: DependencyList = []) => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const notifService = useService(NotificationService)
    const notifTemplateSvc = useService(NotificationTemplateService)
    const orgNotifSvc = useService(AccountNotificationService)

    const {
        level,
        formType,
        formMode,
        domain,
        account,
        builder,
        requestedNotifId,
        requestedTemplateId,
        requestedTestId,
        validateSubmit,
        resolveParentRoute: resolveParentRoute,
        consumerOnSubmitCompletedHandler: onSubmitCompleted,
        consumerSubmitHandler: onSubmit,
    } = props

    if (level === 'org' && !account) {
        throw new Error('useNotificationBuilderViewSubmitCallback: account is required for org level')
    }

    const goBackToParentView = useNotificationBuilderViewBackToParentViewCallback({
        level,
        domainId: domain.id,
        orgId: account?.id,
        resolveParentRoute,
    })

    const handleSubmitCompleted = useCallback(
        async (successful: boolean, isDraft: boolean) => {
            if (onSubmitCompleted) {
                return onSubmitCompleted(successful, isDraft)
            }

            if (successful) {
                goBackToParentView(isDraft)
            }
        },
        [onSubmitCompleted, goBackToParentView],
    )

    return useCallback(
        async (isDraft: boolean = false): Promise<void> => {
            if (onSubmit) {
                return onSubmit(isDraft, builder)
            }

            const isOrgLevel = level === 'org' && !!account
            const isDomainLevel = level === 'domain' && !!domain
            const isNotifForm = formType === 'send'
            const isTemplateForm = formType === 'named-template'
            const isCreate = formMode === 'create'
            const isTemplateCreate = isTemplateForm && isCreate
            const isTemplatePatch = isTemplateForm && !isCreate
            const isDraftCreate = isNotifForm && isDraft && isCreate
            const isDraftPatch = isNotifForm && isDraft && !isCreate
            const isNotifCreate = isNotifForm && !isDraft && isCreate
            const isNotifPatch = isNotifForm && !isDraft && !isCreate

            const shouldConvertDraftToNotification =
                !isDraft && builder.variants.some((v) => v.getDisplayMeta().isDraft)

            /*
            Short-circuit when submitting a template - templates should not validate fields.
             */
            let valid: boolean
            if (isTemplateForm) {
                valid = true
            } else if (isDraft) {
                valid = await validateSaveDraft(builder)
            } else {
                valid = await validateSubmit(
                    builder,
                    domain,
                    appState.flags,
                    false,
                    shouldConvertDraftToNotification,
                    appService,
                )
            }

            let successful = false

            if (valid) {
                const pkgType = undefined
                const addtMeta = isNotifCreate || isTemplateCreate ? null : undefined

                const builds = buildSubmittedNotificationPackages(
                    domain,
                    builder,
                    appState.flags,
                    pkgType,
                    addtMeta,
                    isNotifCreate || isTemplateCreate,
                )

                const isAbTest = builds.length !== 1
                const isOrgLevelAbTestSend = isOrgLevel && isAbTest
                const isOrgLevelSingleSend = isOrgLevel && !isAbTest
                const isDomainLevelAbTestSend = isDomainLevel && isAbTest
                const isDomainLevelSingleSend = isDomainLevel && !isAbTest

                if (isOrgLevelSingleSend) {
                    if (isNotifCreate) {
                        const res = await orgNotifSvc.create(account.id, builds[0], {
                            showLoadingScreen: true,
                            cancellationKey: 'create-org-notif=',
                            version: ApiVersion.V4,
                        })

                        successful = res.ok
                    } else if (isNotifPatch) {
                        if (!requestedNotifId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedNotifId is required')
                        }

                        const res = await orgNotifSvc.update(account.id, requestedNotifId, builds[0], {
                            showLoadingScreen: true,
                            cancellationKey: 'update-org-notif',
                            version: ApiVersion.V4,
                        })

                        successful = res.ok
                    }
                } else if (isOrgLevelAbTestSend) {
                    /*
                    pass - not currently supported
                     */
                } else if (isDomainLevelSingleSend) {
                    if (isTemplateCreate) {
                        successful = await notifTemplateSvc.createNamedTemplate(domain.id, builds[0], {
                            cancellationKey: 'create-notif-template',
                        })
                    } else if (isDraftCreate) {
                        successful = await notifService.saveDraft(domain.id, builds[0], {
                            cancellationKey: 'create-notif-draft',
                            version: ApiVersion.V4,
                        })
                    } else if (isNotifCreate) {
                        successful = await notifService.sendNotification(domain.id, builds[0], {
                            cancellationKey: 'create-notif',
                            version: ApiVersion.V4,
                        })
                    } else if (isTemplatePatch) {
                        if (!requestedTemplateId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedTemplateId is required')
                        }

                        const res = await notifTemplateSvc.putNamedTemplate(domain.id, requestedTemplateId, builds[0], {
                            cancellationKey: 'update-notif-template',
                        })

                        successful = res.ok
                    } else if (isDraftPatch) {
                        if (!requestedNotifId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedNotifId is required')
                        }

                        const res = await notifService.updateNotificationDraft(domain.id, requestedNotifId, builds[0], {
                            cancellationKey: 'update-notif-draft',
                            version: ApiVersion.V4,
                        })

                        successful = !!res
                    } else if (isNotifPatch) {
                        if (!requestedNotifId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedNotifId is required')
                        }

                        const res = await notifService.updateNotification(domain.id, requestedNotifId, builds[0], {
                            cancellationKey: 'update-notif',
                            version: ApiVersion.V4,
                        })

                        successful = !!res
                    }
                } else if (isDomainLevelAbTestSend) {
                    if (isDraftCreate) {
                        successful = await notifService.createTest(
                            domain.id,
                            builds[0].abTest,
                            builds,
                            'create-notif-draft-test',
                            true,
                        )
                    } else if (isNotifCreate) {
                        successful = await notifService.createTest(
                            domain.id,
                            builds[0].abTest,
                            builds,
                            'create-notif-test',
                        )
                    } else if (isDraftPatch) {
                        if (!requestedTestId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedTestId is required')
                        }

                        successful = await notifService.updateTestDraft(
                            domain.id,
                            requestedTestId,
                            builds[0].abTest,
                            builds,
                            'update-notif-draft-test',
                        )
                    } else if (isNotifPatch) {
                        if (!requestedTestId) {
                            throw new Error('useNotificationBuilderViewSubmitCallback: requestedTestId is required')
                        }

                        successful = await notifService.updateTest(
                            domain.id,
                            requestedTestId,
                            builds[0].abTest,
                            builds,
                            'update-notif-test',
                        )
                    }
                }
            }

            await handleSubmitCompleted(successful, isDraft)
        },
        [
            domain.id,
            builder.variants,
            builder.selectedVariantIdx,
            builder.test,
            builder.editNotificationType,
            appState.flags,
            onSubmit,
            handleSubmitCompleted,
            ...deps,
        ],
    )
}
